import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { IconChartHistogram, IconFileTypePdf, IconPhoneCall } from '@tabler/icons-react'
import { isEmpty, isNil } from 'lodash'
import { subDays, differenceInDays, format } from 'date-fns'
import classNames from 'classnames'
import queryString from 'query-string'

import { PageHeader } from '@/components/headers/PageHeader'
import { Button } from '@/components/buttons/Button'
import { OrganizationFilter } from '@/components/filters/OrganizationFilter'
import { FilterForm } from '@/components/filters/FilterForm'
import Select from '@/components/forms/Select'
import { DateRangePickerReusable } from '@/components/datePickers/DateRangePickerReusable'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import NoData from '@/components/NoData'
import { BaltoLogo } from '@/components/BaltoLogo'
import { FormField } from '@/components/forms/FormField'
import { getOrganizationOptions } from '@/utils/helpers'
import { usePrintToPdf } from '@/utils/hooks/usePrintToPdf'
import { fetchVoiceAgents } from '@/reducers/agentProvisioning/agentProvisioning.actions'
import { fetchCampaigns } from '@/reducers/outbound/outboundCampaigns.actions'
import { VoiceAgentsAnalytics } from '@/views/VoiceAgents/components/VoiceAgentsAnalytics'
import {
  DIRECTION_OPTIONS,
  SCHEDULED_CALL_OPTIONS,
} from '@/views/VoiceAgents/voiceAgentsAnalytics.constants'
import { CampaignDetailPage } from '@/views/Outbound/CampaignDetailPage'

import '@/views/VoiceAgents/VoiceAgentsAnalytics.scss'
import './TogoAnalyticsPage.scss'

const MAX_RANGE_DAYS_EXCLUSIVE = 62

const VOICE_AGENTS_TAB = 'voice-agents'
const OUTBOUND_TAB = 'outbound'

const TAB_LABELS = {
  [VOICE_AGENTS_TAB]: 'AI Agents',
  [OUTBOUND_TAB]: 'Outbound',
}

const TogoEmptyState = ({ headerText, text, testId }) => (
  <div className="togo-analytics__empty" data-testid={testId}>
    <div className="togo-analytics__empty-logo">
      <BaltoLogo width="200" height="150" fill="var(--color-neutral)" />
    </div>
    <div className="togo-analytics__empty-title">{headerText}</div>
    <p className="togo-analytics__empty-description">{text}</p>
  </div>
)

export const TogoAnalyticsPage = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()

  const currentUser = useSelector((state) => state.currentUser)
  const currentOrganization = useSelector((state) => state.currentOrganization)
  const organizations = useSelector((state) => state.organizations)
  const provisioningAgents = useSelector((state) => state.agentProvisioning.agents.items)
  const agentsLoading = useSelector((state) => Boolean(state.agentProvisioning.loading.agents))
  const { data: outboundData, loading: outboundLoading } = useSelector(
    (state) => state.outboundCampaigns
  )
  const { campaigns } = outboundData

  const isBaltoAdmin = currentUser?.organizationid === 1
  const isHierarchyManager = Boolean(currentUser?.hierarchy_manager)
  // Cross-org viewing is admin-only: the voice-agents service authorizes only
  // org 1 for another org, so a manager picking a child org would 403. Everyone
  // else (managers included) is scoped to their own org with no selector.
  const hasOrgDropdown = isBaltoAdmin
  const orgDropdownOptions = useMemo(() => getOrganizationOptions(organizations), [organizations])

  // A hierarchy manager is "viewing their own org" until they switch into a child
  // org via the global switcher (`organizationid` then differs from `own_organization_id`).
  const viewingOwnOrg = currentUser?.organizationid === currentUser?.own_organization_id

  // Tab availability follows the logged-in org's entitlement (currentOrganization),
  // NOT the in-page org dropdown — that dropdown only re-scopes the DATA a
  // manager/admin is already entitled to see. (A global "Switch Organization" does
  // change currentOrganization, so tabs track the impersonated org in that case.)
  const canVoiceAgents = isBaltoAdmin || Boolean(currentOrganization.togo_enabled)
  // Outbound requires the logged-in org to have it enabled; cross-org viewing is
  // limited to hierarchy managers looking at their own child orgs.
  const canOutbound =
    Boolean(currentOrganization.outbound_campaigns_enabled) && (viewingOwnOrg || isHierarchyManager)

  const availableTabs = useMemo(() => {
    const tabs = []
    if (canVoiceAgents) {
      tabs.push(VOICE_AGENTS_TAB)
    }
    if (canOutbound) {
      tabs.push(OUTBOUND_TAB)
    }
    return tabs
  }, [canVoiceAgents, canOutbound])

  const {
    tab: tabParam,
    campaign: campaignParam,
    org: orgParam,
  } = queryString.parse(location.search)
  const activeTab = availableTabs.includes(tabParam) ? tabParam : availableTabs[0]
  const selectedCampaignId = campaignParam ? Number(campaignParam) : null

  // Org scope for the Voice Agents tab. Everyone defaults to their own current org
  // so data loads on arrival; only admins get a selector to switch orgs. Seeded
  // from `?org=` so a deep-link stays scoped.
  const [selectedOrgId, setSelectedOrgId] = useState(() => {
    if (orgParam) {
      return Number(orgParam)
    }
    return currentUser?.organizationid ?? null
  })
  const [selectedAgent, setSelectedAgent] = useState('all')
  const [selectedDirection, setSelectedDirection] = useState('all')
  const [selectedScheduled, setSelectedScheduled] = useState('all')

  // Scheduled calls are an outbound feature. Gate the filter on the selected
  // org's entitlement (admin org picker); fall back to the logged-in org.
  const scheduledFilterOrg =
    hasOrgDropdown && selectedOrgId
      ? organizations.find((organization) => organization.id === selectedOrgId)
      : null
  const hasOutboundEnabled = Boolean(
    scheduledFilterOrg
      ? scheduledFilterOrg.outbound_campaigns_enabled
      : currentOrganization.outbound_campaigns_enabled
  )

  // Trailing 30 days, inclusive of today. Pending dates drive the picker preview;
  // committed dates drive the analytics fetch.
  const initialEnd = new Date()
  const initialStart = subDays(initialEnd, 29)
  const [committedStartDate, setCommittedStartDate] = useState(initialStart)
  const [committedEndDate, setCommittedEndDate] = useState(initialEnd)
  const [pendingStartDate, setPendingStartDate] = useState(initialStart)
  const [pendingEndDate, setPendingEndDate] = useState(initialEnd)
  const [dateInvalid, setDateInvalid] = useState(false)

  const isVoiceAgents = activeTab === VOICE_AGENTS_TAB
  const isOutbound = activeTab === OUTBOUND_TAB

  // The admin-picked org, or the caller's own org (BE default) for everyone else.
  const effectiveOrgId = hasOrgDropdown ? selectedOrgId : null

  // Fetch on arrival for the defaulted org; re-fetch when the admin selector
  // changes. Non-admins have no selector and fall back to their own org.
  useEffect(() => {
    if (!isVoiceAgents) {
      return
    }
    dispatch(fetchVoiceAgents(effectiveOrgId))
  }, [dispatch, isVoiceAgents, effectiveOrgId])

  useEffect(() => {
    // Outbound is always scoped to the caller's own org (the reporting call-log
    // endpoint is own-org only), so no org param is threaded here.
    if (isOutbound) {
      dispatch(fetchCampaigns())
    }
  }, [dispatch, isOutbound])

  const agents = useMemo(
    () =>
      provisioningAgents
        .map((agent) => {
          const hasConfig = !isEmpty(agent.configs)
          return {
            userId: agent.user_id,
            fullName: [agent.owner_first_name, agent.owner_last_name].filter(Boolean).join(' '),
            // Admins see every provisioned agent; customers only see live ones.
            provisioned: hasConfig,
            deployed: hasConfig && Boolean(agent.deployed),
          }
        })
        .filter((agent) => (isBaltoAdmin ? agent.provisioned : agent.deployed)),
    [provisioningAgents, isBaltoAdmin]
  )

  // Once the fetch settles with no agents there's nothing to chart — show the
  // branded no-data state rather than an empty analytics dashboard.
  const hasNoVoiceAgentData = !agentsLoading && isEmpty(agents)

  const agentOptions = [
    { label: 'All agents', value: 'all' },
    ...agents.map((agent) => ({
      label: agent.fullName || `Agent ${agent.userId}`,
      value: agent.userId,
    })),
  ]
  const agentUserIds = agents.map((agent) => agent.userId)

  const campaignOptions = campaigns.map((campaign) => ({
    label: campaign.name,
    value: campaign.id,
  }))

  const handleExportPdf = usePrintToPdf()

  // Nothing to export until the tab actually has something on screen. Boolean
  // rather than isNil: a malformed `?campaign=abc` parses to NaN, which renders
  // the empty state below, and the button has to agree with it.
  const canExportPdf = isVoiceAgents ? !hasNoVoiceAgentData : Boolean(selectedCampaignId)

  // The export drops the filter bar with the rest of the app chrome, so the PDF
  // carries its own line naming the window and scope it was taken under.
  const selectedAgentLabel = agentOptions.find((option) => option.value === selectedAgent)?.label
  const selectedDirectionLabel = DIRECTION_OPTIONS.find(
    (option) => option.value === selectedDirection
  )?.label
  const selectedScheduledLabel = SCHEDULED_CALL_OPTIONS.find(
    (option) => option.value === selectedScheduled
  )?.label
  const printContext = [
    `${format(committedStartDate, 'MMM d, yyyy')} – ${format(committedEndDate, 'MMM d, yyyy')}`,
    selectedAgentLabel,
    selectedDirectionLabel,
    hasOutboundEnabled ? `Scheduled calls: ${selectedScheduledLabel}` : null,
  ]
    .filter(Boolean)
    .join(' • ')

  const updateQuery = (updates) => {
    const next = { ...queryString.parse(location.search), ...updates }
    Object.keys(updates).forEach((key) => {
      if (isNil(updates[key])) {
        delete next[key]
      }
    })
    history.replace({ pathname: location.pathname, search: queryString.stringify(next) })
  }

  const handleTabClick = (tabKey) => {
    if (tabKey === activeTab) {
      return
    }
    // Leaving Outbound drops the campaign selection so it doesn't linger in the URL.
    updateQuery({ tab: tabKey, campaign: null })
  }

  const handleOrgSelect = (option, action) => {
    setSelectedOrgId(action?.action === 'clear' ? null : option.value)
  }

  const handleDateRangeChange = ({ selection }) => {
    if (!selection?.startDate || !selection?.endDate) {
      return
    }
    setPendingStartDate(selection.startDate)
    setPendingEndDate(selection.endDate)
    const invalid =
      differenceInDays(selection.endDate, selection.startDate) > MAX_RANGE_DAYS_EXCLUSIVE
    setDateInvalid(invalid)
    if (!invalid) {
      setCommittedStartDate(selection.startDate)
      setCommittedEndDate(selection.endDate)
    }
  }

  // Closing on an over-long range snaps the picker back to the committed window
  // so it never lingers in the error state.
  const handleDateRangeClose = () => {
    if (dateInvalid) {
      setPendingStartDate(committedStartDate)
      setPendingEndDate(committedEndDate)
      setDateInvalid(false)
    }
  }

  if (isEmpty(availableTabs)) {
    return (
      <div data-testid="togo-analytics-page">
        <PageHeader title="Analytics" />
        <NoData
          headerText="No analytics available"
          text="This organization has no Togo analytics enabled."
        />
      </div>
    )
  }

  return (
    <div data-testid="togo-analytics-page">
      <PageHeader
        title="Analytics"
        actions={
          canExportPdf && (
            <Button
              className="print-hidden"
              onClick={handleExportPdf}
              icon={<IconFileTypePdf />}
              title="Save the dashboard as a PDF"
              data-testid="togo-export-pdf"
            >
              Export PDF
            </Button>
          )
        }
      />

      {availableTabs.length > 1 && (
        <div className="va-tabs print-hidden" role="tablist" data-testid="togo-analytics-tabs">
          {availableTabs.map((tabKey) => (
            <button
              key={tabKey}
              type="button"
              role="tab"
              aria-selected={activeTab === tabKey}
              className={classNames('va-tab', { active: activeTab === tabKey })}
              onClick={() => handleTabClick(tabKey)}
              data-testid={`togo-analytics-tab-${tabKey}`}
            >
              {tabKey === VOICE_AGENTS_TAB ? (
                <IconChartHistogram size={16} />
              ) : (
                <IconPhoneCall size={16} />
              )}
              {TAB_LABELS[tabKey]}
            </button>
          ))}
        </div>
      )}

      {isVoiceAgents && (
        <>
          <FilterForm small className="print-hidden">
            {hasOrgDropdown && (
              <OrganizationFilter
                options={orgDropdownOptions}
                value={selectedOrgId}
                onChange={handleOrgSelect}
                isClearable
              />
            )}
            <FormField>
              <label>Agents</label>
              <Select
                options={agentOptions}
                value={selectedAgent}
                isClearable={false}
                onChange={(option) => setSelectedAgent(option.value)}
              />
            </FormField>
            <FormField>
              <label>Direction</label>
              <Select
                options={DIRECTION_OPTIONS}
                value={selectedDirection}
                isClearable={false}
                onChange={(option) => setSelectedDirection(option.value)}
              />
            </FormField>
            {hasOutboundEnabled && (
              <FormField>
                <label>Scheduled Calls</label>
                <Select
                  options={SCHEDULED_CALL_OPTIONS}
                  value={selectedScheduled}
                  isClearable={false}
                  onChange={(option) => setSelectedScheduled(option.value)}
                />
              </FormField>
            )}
            <FormField>
              <label>Date Range</label>
              <DateRangePickerReusable
                data-testid="togo-analytics-date-range"
                startDate={pendingStartDate}
                endDate={pendingEndDate}
                onChange={handleDateRangeChange}
                onClose={handleDateRangeClose}
                error={dateInvalid}
                friendlyLabel
                basicLabel
              />
              {dateInvalid && <ErrorMessage content="Date range must be 63 days or less." />}
            </FormField>
          </FilterForm>

          <p className="print-only togo-analytics__print-context">{printContext}</p>

          {hasNoVoiceAgentData ? (
            <TogoEmptyState
              testId="togo-analytics-empty"
              headerText="No AI agent data yet"
              text="There's no AI agent activity to show for this organization yet."
            />
          ) : (
            <VoiceAgentsAnalytics
              orgId={effectiveOrgId}
              agentId={selectedAgent}
              direction={selectedDirection}
              scheduledFilter={selectedScheduled}
              startDate={committedStartDate}
              endDate={committedEndDate}
              agentUserIds={agentUserIds}
            />
          )}
        </>
      )}

      {isOutbound && (
        <>
          {/* Outbound is own-org only — no org picker (the reporting endpoints
              don't support cross-org). */}
          <FilterForm small className="print-hidden">
            <FormField>
              <label>Campaign</label>
              <Select
                options={campaignOptions}
                value={selectedCampaignId}
                placeholder="Select a campaign"
                isLoading={outboundLoading.campaigns}
                onChange={(option) => updateQuery({ campaign: option?.value })}
              />
            </FormField>
          </FilterForm>

          {selectedCampaignId ? (
            <CampaignDetailPage
              campaignId={selectedCampaignId}
              onBack={() => updateQuery({ campaign: null })}
            />
          ) : (
            <TogoEmptyState
              testId="togo-outbound-empty"
              headerText="No campaign selected"
              text="Choose a campaign above to view its outbound analytics."
            />
          )}
        </>
      )}
    </div>
  )
}

export default TogoAnalyticsPage
